/**
 * Headless live-data runtime, mounted once by the Shell. Follows
 * useTwin.liveSource and the loaded element:
 *   - 'off'  : nothing runs.
 *   - 'sim'  : the simulator (sim/session.ts) drives the loaded element.
 *   - 'live' : the four RFIMS site twins get the real feed (liveFeed.ts); the
 *              simulator also runs, skipping every key that holds a live
 *              reading, so non-RFIMS elements still move and badge SIM.
 * Sessions stop and restart when the source or the element changes. When the
 * hub reports that the user's session has ended (the live client's
 * onSessionEnded), one toast says so and the session query is invalidated:
 * its refetch answers 401 and the app's Protected route sends the user to
 * /login.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/toast';
import { useTwin } from '../store';
import { usePresenter } from '@/features/presenter';
import { startSimulationSession } from '../sim/session';
import { isLiveSite } from './influxBridge';
import { startLiveFeed } from './liveFeed';

/** The session query's key, as src/features/auth/useSession.ts declares it. */
const SESSION_QUERY_KEY = ['session'] as const;

export function TwinLiveRuntime() {
  const liveSourceSetting = useTwin((s) => s.liveSource);
  const demo = usePresenter((s) => s.demoProfile);
  // The presenter's demo profile keeps every reading simulated, so a dark feed cannot surface mid-demo.
  const elementId = useTwin((s) => s.element?.id ?? null);
  const liveSource = isLiveSite(elementId) ? 'live' : demo && liveSourceSetting === 'live' ? 'sim' : liveSourceSetting;
  const hasScenarios = useTwin((s) => s.failureScenarios.length > 0);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (liveSource !== 'live' || !isLiveSite(elementId)) return;
    const session = startLiveFeed(elementId, {
      onSessionEnded: () => {
        toast('warn', 'Live feed closed: the session has ended');
        void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      },
    });
    return () => session.stop();
  }, [liveSource, elementId, queryClient]);

  useEffect(() => {
    if (liveSource === 'off' || !elementId || isLiveSite(elementId)) return;
    const element = useTwin.getState().element;
    if (!element || element.id !== elementId) return;
    const session = startSimulationSession(element);
    return () => session.stop();
  }, [liveSource, elementId]);

  // Starting a failure scenario switches the simulator on, as the old menu auto-started Live Mode.
  useEffect(() => {
    if (hasScenarios && liveSource === 'off') useTwin.getState().setLiveSource('sim');
  }, [hasScenarios, liveSource]);

  return null;
}
